import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type TransitionEvent,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import type { ReaderParagraph } from "@/data/curatedChapters";
import { readerChapterHref } from "@/data/libraryBooks";
import {
  applyParagraphEnhancementsToBlocks,
  buildChapterEnhanceChunks,
  chapterEnhancementCacheKey,
  chapterPlainTextFromParagraphs,
  enhancementCacheKey,
  enhancedSpreadToDisplayPages,
  ENHANCEMENT_PRESET_OPTIONS,
  mergeEnhancedChunkIntoParallel,
  parseEnhanceStyleOnlyPayload,
  parseEnhanceTransformChunkPayload,
  parseEnhanceWebhookPayload,
  type CachedEnhancement,
  type ChapterEnhanceManifest,
  type EnhancementPreset,
  type EnhancementScope,
  type EnhancementStatus,
} from "@/lib/readerEnhancement";
import {
  coerceReaderResumePayload,
  loadReaderBookmark,
  saveReaderBookmark,
  type ReaderResumePayload,
} from "@/lib/readerBookmark";
import { requestSceneImage, revokeSceneObjectUrl } from "@/lib/aistorycastSceneImage";
import {
  chunkParagraphsIntoPages,
  normalizeReaderParagraphsList,
  paragraphIndexToFlatLeftIndex,
  paragraphsToPlainText,
} from "./bookPagination";
import { CastCharactersPanel } from "./CastCharactersPanel";

type JsonObject = Record<string, unknown>;
type ReadingMode = "exact" | "enhanced";
type NarrationStatus = "idle" | "generating" | "ready" | "error";
type NarrationScope = "section" | "chapter";
type StoryGuideStatus = "idle" | "reading" | "paused" | "answering" | "error";
type StoryGuideMode = "exact" | "simple" | "dramatic" | "analysis";

function isObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function firstAudioUrlFromList(value: unknown): string | null {
  if (!Array.isArray(value)) return null;
  for (const u of value) {
    if (typeof u === "string" && u.trim().length > 0) return u.trim();
  }
  return null;
}

/** Spread narration returns `audio_url`; full-chapter map/reduce returns `audio_urls` (play first segment in UI). */
function parseNarrationAudioUrl(payload: unknown): string | null {
  if (isObject(payload)) {
    if (typeof payload.audio_url === "string" && payload.audio_url.length > 0) return payload.audio_url;
    const fromList = firstAudioUrlFromList(payload.audio_urls);
    if (fromList) return fromList;
    if (typeof payload.url === "string" && payload.url.length > 0) return payload.url;
    if (isObject(payload.data)) {
      const d = payload.data;
      if (typeof d.audio_url === "string" && d.audio_url.length > 0) return d.audio_url;
      const nested = firstAudioUrlFromList(d.audio_urls);
      if (nested) return nested;
    }
  }

  if (Array.isArray(payload) && payload.length > 0) {
    const first = payload[0];
    if (isObject(first)) {
      if (typeof first.audio_url === "string" && first.audio_url.length > 0) return first.audio_url;
      const arr = firstAudioUrlFromList(first.audio_urls);
      if (arr) return arr;
    }
  }

  return null;
}

function formatNarrationWebhookErrorPayload(payload: unknown): string | null {
  if (!isObject(payload)) return null;
  const stage = typeof payload.stage === "string" && payload.stage.trim() ? payload.stage.trim() : null;
  const err = typeof payload.error === "string" && payload.error.trim() ? payload.error.trim() : null;
  const msg = typeof payload.message === "string" && payload.message.trim() ? payload.message.trim() : null;
  const bits = [stage ? `stage: ${stage}` : null, err, msg].filter(Boolean) as string[];
  return bits.length > 0 ? bits.join(" — ") : null;
}

function narrationPayloadKeysHint(payload: unknown): string {
  if (!isObject(payload)) return "non-object body";
  return Object.keys(payload).slice(0, 14).join(", ");
}

const paperFaceBg = [
  "radial-gradient(ellipse 120% 45% at 50% 0%, rgba(255,255,255,0.24) 0%, transparent 52%)",
  "repeating-linear-gradient(0deg, transparent 0px, transparent 20px, rgba(88,70,48,0.04) 20px, rgba(88,70,48,0.04) 21px)",
  "repeating-linear-gradient(90deg, transparent 0px, transparent 26px, rgba(72,56,40,0.03) 26px, rgba(72,56,40,0.03) 27px)",
  "linear-gradient(162deg, #fdf8ee 0%, #f2e8d4 40%, #e5d6be 100%)",
].join(", ");

/** Physical stacked sheet layers (offset divs) on outside fore-edge. */
function CascadingPaperSheets(props: { side: "left" | "right" }) {
  const { side } = props;
  const count = 10;
  const outward = side === "left";
  return (
    <div
      className={`pointer-events-none absolute inset-y-2 bottom-3 z-0 ${outward ? "left-0" : "right-0"}`}
      style={{ width: "min(32px, 7%)" }}
      aria-hidden
    >
      {Array.from({ length: count }).map((_, i) => {
        const step = 1.65;
        const shift = 3 + i * step;
        return (
          <div
            key={i}
            className="absolute top-1.5 bottom-2 rounded-[4px]"
            style={{
              width: "calc(100% - 4px)",
              ...(outward
                ? { left: -shift, boxShadow: "-2px 1px 4px rgba(0,0,0,0.12), inset 1px 0 0 rgba(255,255,255,0.25)" }
                : { right: -shift, boxShadow: "2px 1px 4px rgba(0,0,0,0.12), inset -1px 0 0 rgba(255,255,255,0.22)" }),
              background: "linear-gradient(180deg, #fffdf8 0%, #efe4d2 42%, #d8c8a8 100%)",
              opacity: Math.max(0.12, 0.82 - i * 0.08),
              zIndex: -i,
              border: "1px solid rgba(55,42,28,0.06)",
            }}
          />
        );
      })}
    </div>
  );
}

/** Thin paper-edge lines for fore-edge / tail (Da Vinci–style stack illusion). */
function PaperEdgeLines(props: { side: "left" | "right"; className?: string }) {
  const { side, className = "" } = props;
  const lines = 18;
  const toward = side === "left" ? "left" : "right";
  return (
    <div
      className={`pointer-events-none absolute inset-y-2 z-[1] overflow-hidden ${toward === "left" ? "left-0" : "right-0"} w-[11px] sm:w-[13px] ${className}`}
      aria-hidden
    >
      {Array.from({ length: lines }).map((_, i) => {
        const offset = i * 0.75;
        const style: CSSProperties =
          toward === "left"
            ? { left: offset, width: 1.25, top: "4%", bottom: "4%" }
            : { right: offset, width: 1.25, top: "4%", bottom: "4%" };
        return (
          <div
            key={i}
            className="absolute rounded-[1px] bg-gradient-to-b from-[#fffdf6] via-[#e8dcc8] to-[#c9b89a]"
            style={{
              ...style,
              opacity: 0.55 - i * 0.028,
              boxShadow: toward === "left" ? "1px 0 0 rgba(0,0,0,0.12)" : "-1px 0 0 rgba(0,0,0,0.12)",
            }}
          />
        );
      })}
    </div>
  );
}

/** Extra fore-edge thickness via stacked box-shadows (under sheets). */
function PageBlockStack(props: { side: "left" | "right" }) {
  const { side } = props;
  const toward = side === "left" ? "left" : "right";
  const shadowLayers =
    side === "left"
      ? [
          "inset -2px 0 4px rgba(0,0,0,0.18)",
          "-1px 0 0 rgba(252,246,232,0.95)",
          "-2px 0 0 rgba(240,228,210,0.9)",
          "-4px 0 0 rgba(225,205,178,0.75)",
          "-7px 0 0 rgba(200,175,145,0.55)",
          "-11px 0 0 rgba(165,140,110,0.4)",
          "-16px 0 0 rgba(120,98,75,0.28)",
          "-21px 0 0 rgba(70,55,40,0.18)",
          "-26px 0 0 rgba(30,24,18,0.1)",
        ].join(", ")
      : [
          "inset 2px 0 4px rgba(0,0,0,0.18)",
          "1px 0 0 rgba(252,246,232,0.95)",
          "2px 0 0 rgba(240,228,210,0.9)",
          "4px 0 0 rgba(225,205,178,0.75)",
          "7px 0 0 rgba(200,175,145,0.55)",
          "11px 0 0 rgba(165,140,110,0.4)",
          "16px 0 0 rgba(120,98,75,0.28)",
          "21px 0 0 rgba(70,55,40,0.18)",
          "26px 0 0 rgba(30,24,18,0.1)",
        ].join(", ");

  return (
    <div
      className={`pointer-events-none absolute inset-y-1 z-0 ${toward === "left" ? "left-0" : "right-0"} w-6 sm:w-7`}
      style={{ boxShadow: shadowLayers }}
      aria-hidden
    />
  );
}

/** Bottom “block” shadow under open pages. */
function BottomPageStackShadow() {
  return (
    <div
      className="pointer-events-none absolute -bottom-1 left-[6%] right-[6%] z-0 h-4 sm:h-5"
      style={{
        background: "linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, transparent 100%)",
        borderRadius: "0 0 40% 40% / 0 0 100% 100%",
        filter: "blur(4px)",
        opacity: 0.85,
      }}
      aria-hidden
    />
  );
}

/** Horizontal ruled texture on outer fore-edge (paper fibers). */
function ForeEdgeHorizontalRidges(props: { side: "left" | "right" }) {
  const edge = props.side === "left" ? "left-0" : "right-0";
  return (
    <div
      className={`pointer-events-none absolute inset-y-[16%] bottom-2 z-[2] w-[7px] sm:w-[8px] ${edge}`}
      aria-hidden
      style={{
        background: [
          "repeating-linear-gradient(0deg, transparent 0px, transparent 2px, rgba(62,48,32,0.12) 2px, rgba(62,48,32,0.12) 3px)",
          props.side === "left"
            ? "linear-gradient(90deg, rgba(255,255,255,0.2), transparent)"
            : "linear-gradient(270deg, rgba(255,255,255,0.2), transparent)",
        ].join(","),
        opacity: 0.6,
      }}
    />
  );
}

/** Bottom of each page column: visible sheet stack stepping toward desk. */
function PageColumnBottomStack(props: { side: "left" | "right" }) {
  const inset = props.side === "left" ? "left-1 right-3" : "left-3 right-1";
  const n = 10;
  return (
    <div className={`pointer-events-none absolute bottom-0 z-[1] h-[13px] overflow-hidden ${inset}`} aria-hidden>
      {Array.from({ length: n }).map((_, i) => (
        <div
          key={i}
          className="absolute left-[2%] right-[2%] rounded-[1px] bg-gradient-to-r from-[#b0a080] via-[#f8f0e0] to-[#b0a080]"
          style={{
            bottom: i * 0.72,
            height: 1.2,
            opacity: 0.5 - i * 0.038,
            boxShadow: "0 1px 0 rgba(0,0,0,0.15)",
          }}
        />
      ))}
    </div>
  );
}

function PageStackEdges(props: { side: "left" | "right" }) {
  return (
    <>
      <CascadingPaperSheets side={props.side} />
      <PageBlockStack side={props.side} />
      <PaperEdgeLines side={props.side} />
      <ForeEdgeHorizontalRidges side={props.side} />
    </>
  );
}

/** Curved spine valley between pages (no flat black bar). */
function CenterGutterValley() {
  return (
    <div className="relative z-[4] hidden w-[24px] shrink-0 overflow-visible sm:block lg:w-[32px]" aria-hidden>
      <div
        className="pointer-events-none absolute inset-y-0 -left-1.5 -right-1.5 rounded-[999px]"
        style={{
          background:
            "radial-gradient(ellipse 42% 100% at 50% 50%, rgba(6,4,3,0.92) 0%, rgba(26,18,12,0.5) 44%, rgba(68,54,38,0.2) 72%, rgba(110,92,68,0.08) 100%)",
          boxShadow:
            "inset 0 0 16px rgba(0,0,0,0.75), inset 6px 0 12px rgba(0,0,0,0.22), inset -6px 0 12px rgba(0,0,0,0.22)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-y-6 left-0 w-[48%] rounded-l-full opacity-[0.88]"
        style={{
          background:
            "linear-gradient(90deg, rgba(255,252,244,0.62) 0%, rgba(255,246,220,0.2) 58%, transparent 100%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-y-6 right-0 w-[48%] rounded-r-full opacity-[0.88]"
        style={{
          background:
            "linear-gradient(270deg, rgba(255,252,244,0.62) 0%, rgba(255,246,220,0.2) 58%, transparent 100%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-y-8 left-1/2 w-[2px] -translate-x-1/2 rounded-full opacity-75"
        style={{
          background: "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.5) 18%, rgba(0,0,0,0.68) 50%, rgba(0,0,0,0.5) 82%, transparent 100%)",
          boxShadow: "0 0 12px rgba(0,0,0,0.5), 0 0 5px rgba(255,228,190,0.14)",
        }}
      />
    </div>
  );
}

function useReaderLayout() {
  const [wide, setWide] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 900px)").matches : true,
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 900px)");
    const fn = () => setWide(mq.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  /* Tuned for wide landscape faces (no in-page scroll). */
  const maxCharsPerPage = wide ? 720 : 480;
  return { wide, maxCharsPerPage };
}

/** Open page parchment (labels + body). Stack edges belong on `PageBlockColumn`. */
function PageFace(props: {
  paragraphs: ReaderParagraph[];
  side: "left" | "right";
  empty?: boolean;
  /** When true, omit the small caps label row if a paragraph's label is blank (e.g. enhanced prose blocks). */
  hideParagraphLabels?: boolean;
}) {
  const { paragraphs, side, empty, hideParagraphLabels } = props;
  const towardGutter = side === "left" ? "right" : "left";
  const roundMain = side === "left" ? "rounded-l-[16px] rounded-r-[6px]" : "rounded-r-[16px] rounded-l-[6px]";

  const foreEdgeShadow =
    side === "left"
      ? "2px 0 0 rgba(250,244,232,0.55), 5px 0 0 rgba(210,190,160,0.38), 10px 0 0 rgba(175,150,120,0.26), 16px 0 0 rgba(0,0,0,0.08)"
      : "-2px 0 0 rgba(250,244,232,0.55), -5px 0 0 rgba(210,190,160,0.38), -10px 0 0 rgba(175,150,120,0.26), -16px 0 0 rgba(0,0,0,0.08)";

  const curveShadow =
    side === "left"
      ? "inset 0 -10px 14px rgba(55,42,28,0.08), inset 18px 0 32px rgba(55,42,28,0.09), inset -2px 0 10px rgba(255,255,255,0.14)"
      : "inset 0 -10px 14px rgba(55,42,28,0.08), inset -18px 0 32px rgba(55,42,28,0.09), inset 2px 0 10px rgba(255,255,255,0.14)";

  const faceShadow =
    side === "left"
      ? `${curveShadow}, inset 0 -4px 10px rgba(255,255,255,0.32), 0 10px 20px rgba(0,0,0,0.18), ${foreEdgeShadow}`
      : `${curveShadow}, inset 0 -4px 10px rgba(255,255,255,0.32), 0 10px 20px rgba(0,0,0,0.18), ${foreEdgeShadow}`;

  return (
    <div className={`relative min-h-0 min-w-0 flex-1 overflow-visible ${roundMain}`}>
      {!empty && (
        <div
          className={`pointer-events-none absolute inset-y-3 z-[2] ${towardGutter === "right" ? "right-0" : "left-0"} w-[6px] sm:w-[8px]`}
          style={{
            background:
              towardGutter === "right"
                ? "linear-gradient(90deg, rgba(0,0,0,0.14) 0%, rgba(245,236,218,0.45) 42%, rgba(255,255,255,0.12) 100%)"
                : "linear-gradient(270deg, rgba(0,0,0,0.14) 0%, rgba(245,236,218,0.45) 42%, rgba(255,255,255,0.12) 100%)",
            boxShadow: towardGutter === "right" ? "inset 2px 0 4px rgba(0,0,0,0.12)" : "inset -2px 0 4px rgba(0,0,0,0.12)",
          }}
        />
      )}

      <div
        className={[
          "relative z-[3] flex min-h-[min(16vh,138px)] flex-1 flex-col overflow-hidden sm:min-h-[min(17vh,148px)] lg:min-h-[min(18vh,158px)] xl:min-h-[min(19vh,168px)]",
          roundMain,
          empty ? "items-stretch justify-stretch" : "",
        ].join(" ")}
        style={{
          backgroundImage: empty ? "linear-gradient(148deg, #f4ead8 0%, #e8dcc4 48%, #ddd0b8 100%)" : paperFaceBg,
          backgroundColor: "#f0e6d6",
          boxShadow: faceShadow,
          border: "1px solid rgba(62,48,28,0.09)",
          transform: side === "left" ? "rotateX(0.65deg)" : "rotateX(0.65deg)",
          transformOrigin: side === "left" ? "right center" : "left center",
        }}
      >
        {!empty && (
          <div
            className="pointer-events-none absolute inset-[8px] rounded-[5px] opacity-40 sm:inset-[12px] lg:inset-[14px]"
            style={{
              border: "1px solid rgba(90,72,52,0.07)",
              boxShadow: "inset 0 0 36px rgba(90,72,52,0.045)",
            }}
          />
        )}

        {!empty && (
          <div
            className="pointer-events-none absolute inset-0 opacity-40"
            style={{
              background:
                side === "left"
                  ? "linear-gradient(90deg, rgba(255,255,255,0.38) 0%, transparent 20%, transparent 100%)"
                  : "linear-gradient(270deg, rgba(255,255,255,0.38) 0%, transparent 20%, transparent 100%)",
            }}
          />
        )}

        {empty ? (
          <div className="h-full min-h-[inherit] w-full bg-transparent" aria-hidden />
        ) : (
          <div className="relative z-[2] flex flex-1 flex-col gap-2.5 overflow-hidden px-4 py-4 sm:gap-3 sm:px-8 sm:py-5 lg:px-10 lg:py-5">
            {paragraphs.map((p, idx) => (
              <div key={idx}>
                {!(hideParagraphLabels && !String(p.label ?? "").trim()) ? (
                  <p
                    className="text-[0.58rem] font-semibold uppercase tracking-[0.18em] text-[#6e5c42] sm:text-[0.6rem]"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {p.label}
                  </p>
                ) : null}
                <p
                  className={[
                    "text-[0.95rem] leading-[1.62] text-[#241c14] sm:text-[1.04rem] lg:text-[1.07rem]",
                    hideParagraphLabels && !String(p.label ?? "").trim() ? "mt-0" : "mt-0.5",
                  ].join(" ")}
                  style={{
                    fontFamily: "'Lora', 'Georgia', serif",
                    textShadow: "0 0.5px 0 rgba(255,255,255,0.35)",
                  }}
                >
                  <span className="whitespace-pre-wrap">{p.text}</span>
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** One side of the spread: stacked edges + bottom sheets + top page (rotates in 3D with block). */
function PageBlockColumn(props: {
  side: "left" | "right";
  transform: string;
  transformOrigin: string;
  paragraphs: ReaderParagraph[];
  empty?: boolean;
  transformTransition?: string;
  onTransformTransitionEnd?: (event: TransitionEvent<HTMLDivElement>) => void;
  flipElevate?: boolean;
  hideParagraphLabels?: boolean;
}) {
  const {
    side,
    transform,
    transformOrigin,
    paragraphs,
    empty,
    transformTransition,
    onTransformTransitionEnd,
    flipElevate,
    hideParagraphLabels,
  } = props;
  return (
    <div
      className="relative flex min-h-0 min-w-0 flex-1 basis-[50%] flex-col overflow-visible"
      style={{
        transform,
        transformOrigin,
        transformStyle: "preserve-3d",
        transition: transformTransition,
        zIndex: flipElevate ? 8 : undefined,
        willChange: flipElevate ? "transform" : undefined,
      }}
      data-book-part={side === "left" ? "page-block-left" : "page-block-right"}
      onTransitionEnd={onTransformTransitionEnd}
    >
      <div className="pointer-events-none absolute inset-0 z-0 overflow-visible" aria-hidden>
        <PageStackEdges side={side} />
      </div>
      <PageColumnBottomStack side={side} />
      <div className="relative z-[2] flex min-h-0 min-w-0 flex-1" data-book-part={side === "left" ? "left-top-page" : "right-top-page"}>
        <PageFace paragraphs={paragraphs} side={side} empty={empty} hideParagraphLabels={hideParagraphLabels} />
      </div>
    </div>
  );
}

export type ReaderChapterListEntry = { slug: string; title: string };

export type ReaderSearchBlock = {
  chapterSlug: string;
  paragraphIndex: number;
  label: string;
  text: string;
};

export type { ReaderResumePayload };

function normalizeChapterListEntries(input: unknown): ReaderChapterListEntry[] {
  if (!Array.isArray(input)) return [];
  const out: ReaderChapterListEntry[] = [];
  for (const row of input) {
    if (!row || typeof row !== "object") continue;
    const o = row as Record<string, unknown>;
    if (typeof o.slug !== "string" || o.slug.trim().length === 0) continue;
    const title = typeof o.title === "string" && o.title.trim().length > 0 ? o.title.trim() : o.slug.trim();
    out.push({ slug: o.slug.trim(), title });
  }
  return out;
}

function normalizeSearchBlocksInput(input: unknown): ReaderSearchBlock[] {
  if (!Array.isArray(input)) return [];
  const out: ReaderSearchBlock[] = [];
  for (const raw of input) {
    if (!raw || typeof raw !== "object") continue;
    const o = raw as Record<string, unknown>;
    if (typeof o.chapterSlug !== "string" || o.chapterSlug.trim().length === 0) continue;
    const pi =
      typeof o.paragraphIndex === "number" && Number.isFinite(o.paragraphIndex)
        ? Math.max(0, Math.floor(o.paragraphIndex))
        : 0;
    const label = typeof o.label === "string" ? o.label : "Narrator";
    const text = typeof o.text === "string" ? o.text : "";
    out.push({ chapterSlug: o.chapterSlug.trim(), paragraphIndex: pi, label, text });
  }
  return out;
}

export type ImmersiveBookReaderProps = {
  bookId: string;
  chapterSlug: string;
  bookTitle: string;
  author: string;
  chapterHeading: string;
  paragraphs: ReaderParagraph[];
  /** Table of contents for the current book (when metadata exists). */
  chapterList?: ReaderChapterListEntry[] | null;
  /** Flattened paragraphs across chapters for sidebar search (MVP: built on the client). */
  bookSearchBlocks?: ReaderSearchBlock[] | null;
  bookSearchLoading?: boolean;
  /** Applied once when navigating from search/resume with router state. */
  initialReaderResume?: ReaderResumePayload | unknown | null;
  onReaderResumeConsumed?: () => void;
  /** True when this book has imported chapter/full text in IndexedDB (cast analysis + Characters tab). */
  castAnalysisEligible?: boolean;
};

type CompanionTab = "chapters" | "characters" | "scene" | "analysis" | "secrets" | "storyGuide";

const CHAPTER_STYLE_EXCERPT_MAX = 12000;

export function ImmersiveBookReader(props: ImmersiveBookReaderProps) {
  const {
    bookId,
    chapterSlug,
    bookTitle,
    author,
    chapterHeading,
    paragraphs: paragraphsProp,
    chapterList: chapterListProp = [],
    bookSearchBlocks: bookSearchBlocksProp = [],
    bookSearchLoading = false,
    initialReaderResume: initialReaderResumeRaw = null,
    onReaderResumeConsumed,
    castAnalysisEligible = false,
  } = props;
  const navigate = useNavigate();

  const chapterList = useMemo(() => normalizeChapterListEntries(chapterListProp), [chapterListProp]);
  const bookSearchBlocks = useMemo(() => normalizeSearchBlocksInput(bookSearchBlocksProp), [bookSearchBlocksProp]);
  const normalizedResume = useMemo(
    () => coerceReaderResumePayload(initialReaderResumeRaw),
    [initialReaderResumeRaw],
  );

  const readerParagraphs = useMemo(() => normalizeReaderParagraphsList(paragraphsProp), [paragraphsProp]);
  const narrationWebhookUrl = import.meta.env.VITE_AISTORYCAST_NARRATION_WEBHOOK_URL as string | undefined;
  const storyGuideWebhookUrl = (import.meta.env.VITE_AISTORYCAST_STORY_GUIDE_WEBHOOK_URL as string | undefined) ??
    "https://n8n.jdpenterprises.ai/webhook/aistorycast-story-guide";
  const enhanceWebhookUrl = (import.meta.env.VITE_AISTORYCAST_ENHANCE_WEBHOOK_URL as string | undefined) ??
    "https://n8n.jdpenterprises.ai/webhook/aistorycast-enhance-text";

  const { wide, maxCharsPerPage } = useReaderLayout();
  const pages = useMemo(
    () => chunkParagraphsIntoPages(readerParagraphs, maxCharsPerPage),
    [readerParagraphs, maxCharsPerPage],
  );

  const [flatLeftIndex, setFlatLeftIndex] = useState(0);
  const [sceneUrl, setSceneUrl] = useState<string | null>(null);
  const [sceneFilename, setSceneFilename] = useState<string | null>(null);
  const [sceneError, setSceneError] = useState<string | null>(null);
  const [sceneLoading, setSceneLoading] = useState(false);
  const [companionTab, setCompanionTab] = useState<CompanionTab>("scene");
  const [sidebarSearchQuery, setSidebarSearchQuery] = useState("");
  const [bookmarkVersion, setBookmarkVersion] = useState(0);
  const [readingMode, setReadingMode] = useState<ReadingMode>("exact");
  const [enhancementPreset, setEnhancementPreset] = useState<EnhancementPreset>("modern");
  const [customEnhancementPrompt, setCustomEnhancementPrompt] = useState("");
  const [enhancementScope, setEnhancementScope] = useState<EnhancementScope>("spread");
  const [chapterEnhanceManifest, setChapterEnhanceManifest] = useState<ChapterEnhanceManifest | null>(null);
  const [enhancementStatus, setEnhancementStatus] = useState<EnhancementStatus>("idle");
  const [enhancementError, setEnhancementError] = useState<string | null>(null);
  const [enhancementCache, setEnhancementCache] = useState<Record<string, CachedEnhancement>>({});
  const [narrationStatus, setNarrationStatus] = useState<NarrationStatus>("idle");
  const [narrationAudioUrl, setNarrationAudioUrl] = useState<string | null>(null);
  const [narrationError, setNarrationError] = useState<string | null>(null);
  const [narrationWarning, setNarrationWarning] = useState<string | null>(null);
  const [narrationScope, setNarrationScope] = useState<NarrationScope | null>(null);
  const [storyGuideStatus, setStoryGuideStatus] = useState<StoryGuideStatus>("idle");
  const [storyGuideMode, setStoryGuideMode] = useState<StoryGuideMode>("exact");
  const [storyGuideParagraphIndex, setStoryGuideParagraphIndex] = useState<number>(0);
  const [storyGuideQuestion, setStoryGuideQuestion] = useState("");
  const [storyGuideAnswer, setStoryGuideAnswer] = useState("");
  /** Active 3D page-turn direction; index updates on transition end to avoid flicker. */
  const [pageFlip, setPageFlip] = useState<null | "next" | "prev">(null);
  const pageFlipRef = useRef<null | "next" | "prev">(null);
  const resumeAppliedRef = useRef(false);
  const sceneBlobUrlRef = useRef<string | null>(null);

  const lastSpreadStart = pages.length <= 1 ? 0 : Math.floor((pages.length - 1) / 2) * 2;
  const effectiveMaxLeft = wide ? lastSpreadStart : Math.max(0, pages.length - 1);

  const replaceSceneUrl = useCallback((next: string | null) => {
    if (sceneBlobUrlRef.current && sceneBlobUrlRef.current !== next) {
      revokeSceneObjectUrl(sceneBlobUrlRef.current);
      sceneBlobUrlRef.current = null;
    }
    if (next?.startsWith("blob:")) {
      sceneBlobUrlRef.current = next;
    }
    setSceneUrl(next);
  }, []);

  useEffect(() => {
    setFlatLeftIndex(0);
    replaceSceneUrl(null);
    setSceneFilename(null);
    setSceneError(null);
    setCompanionTab("scene");
    setNarrationStatus("idle");
    setNarrationAudioUrl(null);
    setNarrationError(null);
    setNarrationWarning(null);
    setNarrationScope(null);
    setStoryGuideStatus("idle");
    setStoryGuideMode("exact");
    setStoryGuideParagraphIndex(0);
    setStoryGuideQuestion("");
    setStoryGuideAnswer("");
    setPageFlip(null);
    pageFlipRef.current = null;
    setSidebarSearchQuery("");
    setEnhancementStatus("idle");
    setEnhancementError(null);
    setEnhancementCache({});
    setEnhancementPreset("modern");
    setCustomEnhancementPrompt("");
    setEnhancementScope("spread");
    setChapterEnhanceManifest(null);
  }, [bookId, chapterSlug, replaceSceneUrl]);

  useEffect(() => {
    pageFlipRef.current = pageFlip;
  }, [pageFlip]);

  useEffect(() => {
    setFlatLeftIndex((i) => Math.min(i, effectiveMaxLeft));
  }, [effectiveMaxLeft]);

  useEffect(() => {
    return () => {
      revokeSceneObjectUrl(sceneBlobUrlRef.current);
      sceneBlobUrlRef.current = null;
    };
  }, []);

  const clampedLeft = Math.min(flatLeftIndex, Math.max(0, effectiveMaxLeft));
  const baseLeftPage = pages[clampedLeft] ?? [];
  const baseRightPage = wide ? pages[clampedLeft + 1] ?? null : null;

  const spreadParagraphStartIndex = useMemo(() => {
    if (clampedLeft <= 0) return 0;
    const before = pages.slice(0, clampedLeft);
    return before.reduce((total, page) => total + page.length, 0);
  }, [clampedLeft, pages]);

  const activeEnhancementKey = useMemo(
    () => enhancementCacheKey(bookId, chapterSlug, clampedLeft, enhancementPreset, customEnhancementPrompt),
    [bookId, chapterSlug, clampedLeft, enhancementPreset, customEnhancementPrompt],
  );
  const activeEnhancement = enhancementCache[activeEnhancementKey];

  const chapterEnhancementKey = useMemo(
    () => chapterEnhancementCacheKey(bookId, chapterSlug, enhancementPreset, customEnhancementPrompt),
    [bookId, chapterSlug, enhancementPreset, customEnhancementPrompt],
  );
  const chapterEnhancement = enhancementCache[chapterEnhancementKey];

  const chapterParagraphTexts =
    chapterEnhancement?.paragraphTexts &&
    chapterEnhancement.paragraphTexts.length === readerParagraphs.length
      ? chapterEnhancement.paragraphTexts
      : null;

  const { leftPage, rightPage } = useMemo(() => {
    if (readingMode !== "enhanced") {
      return { leftPage: baseLeftPage, rightPage: baseRightPage };
    }
    if (enhancementScope === "chapter" && chapterParagraphTexts) {
      return {
        leftPage: applyParagraphEnhancementsToBlocks(
          baseLeftPage,
          spreadParagraphStartIndex,
          readerParagraphs,
          chapterParagraphTexts,
        ),
        rightPage:
          baseRightPage && baseRightPage.length > 0
            ? applyParagraphEnhancementsToBlocks(
                baseRightPage,
                spreadParagraphStartIndex + baseLeftPage.length,
                readerParagraphs,
                chapterParagraphTexts,
              )
            : baseRightPage,
      };
    }
    if (enhancementScope === "spread" && activeEnhancement?.enhancedText?.trim()) {
      const { left, right } = enhancedSpreadToDisplayPages(
        activeEnhancement.enhancedText,
        activeEnhancement.styleLabel,
        wide,
      );
      return { leftPage: left, rightPage: right };
    }
    return { leftPage: baseLeftPage, rightPage: baseRightPage };
  }, [
    readingMode,
    enhancementScope,
    chapterParagraphTexts,
    activeEnhancement,
    wide,
    baseLeftPage,
    baseRightPage,
    spreadParagraphStartIndex,
    readerParagraphs,
  ]);

  const spreadPlainTextOriginal = useMemo(() => {
    const parts = [paragraphsToPlainText(baseLeftPage)];
    if (baseRightPage && baseRightPage.length > 0) parts.push(paragraphsToPlainText(baseRightPage));
    return parts.join("\n\n");
  }, [baseLeftPage, baseRightPage]);

  /** Passage shown on the spread (original or enhanced) — scene, labels. */
  const spreadPlainText = useMemo(() => {
    const parts = [paragraphsToPlainText(leftPage)];
    if (rightPage && rightPage.length > 0) parts.push(paragraphsToPlainText(rightPage));
    return parts.join("\n\n");
  }, [leftPage, rightPage]);

  /** Story Guide + similar: use enhanced prose when Enhanced mode and cache hit. */
  const passageForStoryGuide = useMemo(() => {
    if (readingMode === "enhanced" && enhancementScope === "chapter" && chapterEnhancement?.enhancedText?.trim()) {
      return chapterEnhancement.enhancedText.trim();
    }
    if (readingMode === "enhanced" && enhancementScope === "spread" && activeEnhancement?.enhancedText?.trim()) {
      return activeEnhancement.enhancedText.trim();
    }
    return spreadPlainTextOriginal;
  }, [
    readingMode,
    enhancementScope,
    chapterEnhancement,
    activeEnhancement,
    spreadPlainTextOriginal,
  ]);

  const canPrevPage = clampedLeft > 0;
  const canNextPage = wide ? clampedLeft + 2 < pages.length : clampedLeft < pages.length - 1;

  const goPrevPage = useCallback(() => {
    setFlatLeftIndex((i) => {
      const cur = Math.min(i, effectiveMaxLeft);
      if (wide) return Math.max(0, cur - 2);
      return Math.max(0, cur - 1);
    });
  }, [wide, effectiveMaxLeft]);

  const goNextPage = useCallback(() => {
    setFlatLeftIndex((i) => {
      const cur = Math.min(i, effectiveMaxLeft);
      if (wide) return Math.min(lastSpreadStart, cur + 2);
      return Math.min(pages.length - 1, cur + 1);
    });
  }, [wide, effectiveMaxLeft, lastSpreadStart, pages.length]);

  const FLIP_DEG = 168;
  const baseTurnDeg = wide ? 8 : 3.8;

  const beginPageFlip = useCallback(
    (dir: "next" | "prev") => {
      if (pageFlipRef.current !== null) return;
      if (dir === "next" && !canNextPage) return;
      if (dir === "prev" && !canPrevPage) return;
      pageFlipRef.current = dir;
      setPageFlip(dir);
    },
    [canNextPage, canPrevPage],
  );

  const onFlipTransitionEnd = useCallback(
    (event: TransitionEvent<HTMLDivElement>) => {
      if (event.propertyName !== "transform") return;
      if (event.target !== event.currentTarget) return;
      const dir = pageFlipRef.current;
      if (dir !== "next" && dir !== "prev") return;
      if (dir === "next") goNextPage();
      else goPrevPage();
      pageFlipRef.current = null;
      setPageFlip(null);
    },
    [goNextPage, goPrevPage],
  );

  const flipTransition =
    pageFlip !== null ? "transform 0.48s cubic-bezier(0.28, 0.12, 0.18, 1)" : undefined;

  const leftBlockTransform = (() => {
    if (pageFlip === "prev") return `rotateY(${baseTurnDeg + FLIP_DEG}deg)`;
    if (!wide && pageFlip === "next") return `rotateY(${baseTurnDeg - FLIP_DEG}deg)`;
    return `rotateY(${baseTurnDeg}deg)`;
  })();

  const rightBlockTransform = (() => {
    if (wide && pageFlip === "next") return `rotateY(-${baseTurnDeg + FLIP_DEG}deg)`;
    return `rotateY(-${baseTurnDeg}deg)`;
  })();

  const leftFlipListening = pageFlip === "prev" || (!wide && pageFlip === "next");
  const rightFlipListening = wide && pageFlip === "next";

  const chapterPlainText = useMemo(() => paragraphsToPlainText(readerParagraphs), [readerParagraphs]);

  const chapterPlainTextForNarration = useMemo(() => {
    if (readingMode === "enhanced" && chapterParagraphTexts) {
      return chapterPlainTextFromParagraphs(
        readerParagraphs.map((p, i) => ({ ...p, text: chapterParagraphTexts[i] ?? p.text })),
      );
    }
    return chapterPlainText;
  }, [readingMode, chapterParagraphTexts, readerParagraphs, chapterPlainText]);

  useEffect(() => {
    setStoryGuideParagraphIndex(spreadParagraphStartIndex);
  }, [spreadParagraphStartIndex]);

  useEffect(() => {
    resumeAppliedRef.current = false;
  }, [bookId, chapterSlug, normalizedResume?.paragraphIndex, normalizedResume?.flatLeftIndex]);

  useEffect(() => {
    if (resumeAppliedRef.current || !normalizedResume || readerParagraphs.length === 0) return;
    const { paragraphIndex, flatLeftIndex } = normalizedResume;
    try {
      if (typeof paragraphIndex === "number" && paragraphIndex >= 0 && paragraphIndex < readerParagraphs.length) {
        setFlatLeftIndex(paragraphIndexToFlatLeftIndex(readerParagraphs, maxCharsPerPage, wide, paragraphIndex));
      } else if (typeof flatLeftIndex === "number" && flatLeftIndex >= 0) {
        setFlatLeftIndex(Math.min(flatLeftIndex, effectiveMaxLeft));
      }
    } catch {
      /* layout math should not block render */
    }
    resumeAppliedRef.current = true;
    try {
      onReaderResumeConsumed?.();
    } catch {
      /* navigation helper must not crash reader */
    }
  }, [
    normalizedResume,
    readerParagraphs,
    maxCharsPerPage,
    wide,
    effectiveMaxLeft,
    onReaderResumeConsumed,
  ]);

  const chapterTitleBySlug = useMemo(() => {
    const m = new Map<string, string>();
    for (const row of chapterList) {
      m.set(row.slug, row.title);
    }
    return m;
  }, [chapterList]);

  const searchMatches = useMemo(() => {
    try {
      const q = sidebarSearchQuery.trim().toLowerCase();
      if (q.length < 2) return [];
      const out: { block: ReaderSearchBlock; snippet: string }[] = [];
      for (const b of bookSearchBlocks) {
        const combined = `${String(b.label ?? "")}: ${String(b.text ?? "")}`;
        const idx = combined.toLowerCase().indexOf(q);
        if (idx === -1) continue;
        const padStart = Math.max(0, idx - 32);
        const padEnd = Math.min(combined.length, idx + q.length + 56);
        const snippet = `${padStart > 0 ? "…" : ""}${combined.slice(padStart, padEnd)}${padEnd < combined.length ? "…" : ""}`;
        out.push({ block: b, snippet });
        if (out.length >= 28) break;
      }
      return out;
    } catch {
      return [];
    }
  }, [bookSearchBlocks, sidebarSearchQuery]);

  const jumpToSearchBlock = useCallback(
    (block: ReaderSearchBlock) => {
      try {
        if (block.chapterSlug === chapterSlug) {
          setFlatLeftIndex(
            paragraphIndexToFlatLeftIndex(readerParagraphs, maxCharsPerPage, wide, block.paragraphIndex),
          );
        } else {
          navigate(readerChapterHref(bookId, block.chapterSlug), {
            state: { readerResume: { paragraphIndex: block.paragraphIndex } },
          });
        }
      } catch {
        /* search navigation is best-effort */
      }
    },
    [bookId, chapterSlug, readerParagraphs, maxCharsPerPage, wide, navigate],
  );

  const savedBookmark = useMemo(() => loadReaderBookmark(bookId), [bookId, bookmarkVersion]);

  const saveMyPlace = useCallback(() => {
    try {
      saveReaderBookmark({
        bookId,
        chapterSlug,
        flatLeftIndex: clampedLeft,
        paragraphIndex: spreadParagraphStartIndex,
        savedAt: new Date().toISOString(),
      });
      setBookmarkVersion((v) => v + 1);
    } catch {
      /* storage quota / private mode */
    }
  }, [bookId, chapterSlug, clampedLeft, spreadParagraphStartIndex]);

  const resumeSavedPlace = useCallback(() => {
    try {
      const b = loadReaderBookmark(bookId);
      if (!b) return;
      if (b.chapterSlug === chapterSlug) {
        setFlatLeftIndex(paragraphIndexToFlatLeftIndex(readerParagraphs, maxCharsPerPage, wide, b.paragraphIndex));
      } else {
        navigate(readerChapterHref(bookId, b.chapterSlug), {
          state: { readerResume: { paragraphIndex: b.paragraphIndex, flatLeftIndex: b.flatLeftIndex } },
        });
      }
    } catch {
      /* corrupt bookmark or navigation */
    }
  }, [bookId, chapterSlug, readerParagraphs, maxCharsPerPage, wide, navigate]);

  const onShowScene = useCallback(async () => {
    setSceneLoading(true);
    setSceneError(null);
    const result = await requestSceneImage(spreadPlainText);
    setSceneLoading(false);
    if (result.ok) {
      replaceSceneUrl(result.url);
      setSceneFilename(result.filename ?? null);
    } else {
      replaceSceneUrl(null);
      setSceneFilename(null);
      setSceneError(result.error);
    }
  }, [spreadPlainText, replaceSceneUrl]);

  const quickStoryGuideAnswer = useCallback(
    async (preset: "explain" | "simple" | "summary") => {
      let nextQuestion = "";
      let nextMode: StoryGuideMode = "analysis";
      if (preset === "explain") {
        nextQuestion = "Explain this passage clearly and point out the most important meaning.";
        nextMode = "analysis";
      } else if (preset === "simple") {
        nextQuestion = "Read it like I'm 5. Explain this passage in very simple language.";
        nextMode = "simple";
      } else {
        nextQuestion = "Summarize this page in a short, clear way.";
        nextMode = "analysis";
      }
      setStoryGuideMode(nextMode);
      setStoryGuideQuestion(nextQuestion);
      setStoryGuideStatus("answering");
      const nearbyStart = Math.max(0, spreadParagraphStartIndex - 2);
      const nearbyEnd = Math.min(readerParagraphs.length, spreadParagraphStartIndex + 3);
      const recentContext = paragraphsToPlainText(readerParagraphs.slice(nearbyStart, nearbyEnd));
      try {
        const response = await fetch(storyGuideWebhookUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            book_id: bookId,
            chapter_id: chapterSlug,
            current_paragraph_index: storyGuideParagraphIndex,
            story_guide_mode: nextMode,
            question: nextQuestion,
            current_passage: passageForStoryGuide,
            recent_context: recentContext,
          }),
        });
        const raw = await response.text();
        if (!raw.trim()) {
          throw new Error("Story Guide returned an empty response.");
        }
        let payload: unknown;
        try {
          payload = JSON.parse(raw);
        } catch {
          throw new Error("Story Guide returned invalid JSON.");
        }
        if (!response.ok || !isObject(payload) || payload.success !== true || typeof payload.answer !== "string") {
          const message =
            isObject(payload) && typeof payload.error === "string" && payload.error.trim().length > 0
              ? payload.error.trim()
              : `Story Guide request failed (${response.status}).`;
          throw new Error(message);
        }
        setStoryGuideAnswer(payload.answer.trim());
        setStoryGuideStatus("paused");
      } catch (error) {
        setStoryGuideStatus("error");
        setStoryGuideAnswer(error instanceof Error ? error.message : "Story Guide failed.");
      }
    },
    [
      bookId,
      chapterSlug,
      readerParagraphs,
      spreadParagraphStartIndex,
      passageForStoryGuide,
      storyGuideParagraphIndex,
      storyGuideWebhookUrl,
    ],
  );

  const askStoryGuide = useCallback(() => {
    const question = storyGuideQuestion.trim();
    if (!question) {
      setStoryGuideStatus("error");
      setStoryGuideAnswer("Please enter a question for Story Guide.");
      return;
    }
    const nearbyStart = Math.max(0, spreadParagraphStartIndex - 2);
    const nearbyEnd = Math.min(readerParagraphs.length, spreadParagraphStartIndex + 3);
    const recentContext = paragraphsToPlainText(readerParagraphs.slice(nearbyStart, nearbyEnd));
    setStoryGuideStatus("answering");
    void (async () => {
      try {
        const response = await fetch(storyGuideWebhookUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            book_id: bookId,
            chapter_id: chapterSlug,
            current_paragraph_index: storyGuideParagraphIndex,
            story_guide_mode: storyGuideMode,
            question,
            current_passage: passageForStoryGuide,
            recent_context: recentContext,
          }),
        });
        const raw = await response.text();
        if (!raw.trim()) {
          throw new Error("Story Guide returned an empty response.");
        }
        let payload: unknown;
        try {
          payload = JSON.parse(raw);
        } catch {
          throw new Error("Story Guide returned invalid JSON.");
        }
        if (!response.ok || !isObject(payload) || payload.success !== true || typeof payload.answer !== "string") {
          const message =
            isObject(payload) && typeof payload.error === "string" && payload.error.trim().length > 0
              ? payload.error.trim()
              : `Story Guide request failed (${response.status}).`;
          throw new Error(message);
        }
        setStoryGuideAnswer(payload.answer.trim());
        setStoryGuideStatus("paused");
      } catch (error) {
        setStoryGuideStatus("error");
        setStoryGuideAnswer(error instanceof Error ? error.message : "Story Guide failed.");
      }
    })();
  }, [
    bookId,
    chapterSlug,
    readerParagraphs,
    spreadParagraphStartIndex,
    passageForStoryGuide,
    storyGuideMode,
    storyGuideParagraphIndex,
    storyGuideQuestion,
    storyGuideWebhookUrl,
  ]);

  const generateEnhancedSpread = useCallback(async () => {
    if (!enhanceWebhookUrl || enhanceWebhookUrl.trim().length === 0) {
      setEnhancementStatus("error");
      setEnhancementError("Enhancement webhook URL is missing. Set VITE_AISTORYCAST_ENHANCE_WEBHOOK_URL.");
      return;
    }
    if (enhancementPreset === "custom" && customEnhancementPrompt.trim().length === 0) {
      setEnhancementStatus("error");
      setEnhancementError("Enter a custom style description, or choose another preset.");
      return;
    }
    if (spreadPlainTextOriginal.trim().length === 0) {
      setEnhancementStatus("error");
      setEnhancementError("No text on this spread to enhance.");
      return;
    }
    const key = enhancementCacheKey(bookId, chapterSlug, clampedLeft, enhancementPreset, customEnhancementPrompt);
    setEnhancementStatus("generating");
    setEnhancementError(null);
    try {
      const response = await fetch(enhanceWebhookUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          book_id: bookId,
          chapter_id: chapterSlug,
          original_text: spreadPlainTextOriginal,
          preset: enhancementPreset,
          custom_prompt: enhancementPreset === "custom" ? customEnhancementPrompt.trim() : "",
          reading_mode: "enhanced",
          enhance_phase: "spread",
        }),
      });
      const raw = await response.text();
      if (!response.ok) {
        const hint = raw.trim().slice(0, 420);
        throw new Error(
          hint ? `Enhancement request failed (HTTP ${response.status}): ${hint}` : `Enhancement request failed (HTTP ${response.status}).`,
        );
      }
      if (!raw.trim()) throw new Error("Enhancement service returned an empty response.");
      let payload: unknown;
      try {
        payload = JSON.parse(raw);
      } catch {
        throw new Error(
          `Enhancement service returned invalid JSON (HTTP ${response.status}). ${raw.trim().slice(0, 320)}`,
        );
      }
      const parsed = parseEnhanceWebhookPayload(payload);
      if (!parsed.ok) throw new Error(parsed.error);
      setEnhancementCache((prev) => ({
        ...prev,
        [key]: { enhancedText: parsed.text, styleLabel: parsed.styleLabel },
      }));
      setEnhancementStatus("ready");
      setReadingMode("enhanced");
    } catch (e) {
      setEnhancementStatus("error");
      setEnhancementError(e instanceof Error ? e.message : "Enhancement failed.");
    }
  }, [
    enhanceWebhookUrl,
    bookId,
    chapterSlug,
    clampedLeft,
    enhancementPreset,
    customEnhancementPrompt,
    spreadPlainTextOriginal,
  ]);

  const generateChapterEnhancement = useCallback(async () => {
    if (!enhanceWebhookUrl || enhanceWebhookUrl.trim().length === 0) {
      setEnhancementStatus("error");
      setEnhancementError("Enhancement webhook URL is missing. Set VITE_AISTORYCAST_ENHANCE_WEBHOOK_URL.");
      return;
    }
    if (enhancementPreset === "custom" && customEnhancementPrompt.trim().length === 0) {
      setEnhancementStatus("error");
      setEnhancementError("Enter a custom style description, or choose another preset.");
      return;
    }
    if (readerParagraphs.length === 0) {
      setEnhancementStatus("error");
      setEnhancementError("No chapter text to enhance.");
      return;
    }
    const chunks = buildChapterEnhanceChunks(readerParagraphs);
    if (chunks.length === 0) {
      setEnhancementStatus("error");
      setEnhancementError("No chapter text to enhance.");
      return;
    }
    const jobId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `ch_${Date.now()}`;
    const chapterKey = chapterEnhancementCacheKey(
      bookId,
      chapterSlug,
      enhancementPreset,
      customEnhancementPrompt,
    );
    const customSlice = enhancementPreset === "custom" ? customEnhancementPrompt.trim() : "";
    setChapterEnhanceManifest({
      job_id: jobId,
      book_id: bookId,
      chapter_id: chapterSlug,
      preset: enhancementPreset,
      custom_prompt: customSlice,
      style_prompt: "",
      style_label: "",
      status: "processing",
      chunks_total: chunks.length,
      chunks_ready: 0,
      enhanced_chunks: [],
    });
    setEnhancementStatus("generating");
    setEnhancementError(null);
    try {
      const styleExcerpt = chapterPlainTextFromParagraphs(readerParagraphs).slice(0, CHAPTER_STYLE_EXCERPT_MAX);
      const styleResponse = await fetch(enhanceWebhookUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          book_id: bookId,
          chapter_id: chapterSlug,
          original_text: styleExcerpt,
          preset: enhancementPreset,
          custom_prompt: customSlice,
          reading_mode: "enhanced",
          enhance_phase: "style_only",
        }),
      });
      const styleRaw = await styleResponse.text();
      if (!styleResponse.ok) {
        const hint = styleRaw.trim().slice(0, 420);
        throw new Error(
          hint
            ? `Style prompt request failed (HTTP ${styleResponse.status}): ${hint}`
            : `Style prompt request failed (HTTP ${styleResponse.status}).`,
        );
      }
      if (!styleRaw.trim()) throw new Error("Enhancement service returned an empty response.");
      let stylePayload: unknown;
      try {
        stylePayload = JSON.parse(styleRaw);
      } catch {
        throw new Error(
          `Enhancement service returned invalid JSON (HTTP ${styleResponse.status}). ${styleRaw.trim().slice(0, 320)}`,
        );
      }
      const styleParsed = parseEnhanceStyleOnlyPayload(stylePayload);
      if (!styleParsed.ok) throw new Error(styleParsed.error);
      const style_prompt = styleParsed.stylePrompt;
      const style_label = styleParsed.styleLabel;

      setChapterEnhanceManifest((m) =>
        m && m.job_id === jobId ? { ...m, style_prompt, style_label } : m,
      );

      const parallel: (string | null)[] = readerParagraphs.map(() => null);
      let prevTail = "";

      for (let ci = 0; ci < chunks.length; ci++) {
        const ch = chunks[ci];
        const transformResponse = await fetch(enhanceWebhookUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            book_id: bookId,
            chapter_id: chapterSlug,
            preset: enhancementPreset,
            custom_prompt: customSlice,
            reading_mode: "enhanced",
            enhance_phase: "transform_only",
            style_prompt,
            style_label,
            original_text: ch.text,
            chunk_context_tail: prevTail ? prevTail.slice(-1600) : "",
          }),
        });
        const trRaw = await transformResponse.text();
        if (!transformResponse.ok) {
          const hint = trRaw.trim().slice(0, 420);
          throw new Error(
            hint
              ? `Chunk ${ci + 1}/${chunks.length} failed (HTTP ${transformResponse.status}): ${hint}`
              : `Chunk ${ci + 1}/${chunks.length} failed (HTTP ${transformResponse.status}).`,
          );
        }
        if (!trRaw.trim()) throw new Error(`Chunk ${ci + 1}/${chunks.length}: empty response.`);
        let trPayload: unknown;
        try {
          trPayload = JSON.parse(trRaw);
        } catch {
          throw new Error(
            `Chunk ${ci + 1}/${chunks.length}: invalid JSON. ${trRaw.trim().slice(0, 280)}`,
          );
        }
        const parsed = parseEnhanceTransformChunkPayload(trPayload);
        if (!parsed.ok) throw new Error(parsed.error);
        mergeEnhancedChunkIntoParallel(parallel, ch.startParagraphIndex, ch.endParagraphIndex, parsed.text);
        prevTail = parsed.text;

        const paragraphTexts = readerParagraphs.map((p, i) => {
          const pt = parallel[i];
          if (pt != null && String(pt).trim().length > 0) return pt;
          return p.text;
        });
        const enhancedText = chapterPlainTextFromParagraphs(
          readerParagraphs.map((p, i) => ({ ...p, text: paragraphTexts[i] ?? p.text })),
        );

        setEnhancementCache((prev) => ({
          ...prev,
          [chapterKey]: { enhancedText, styleLabel: style_label, paragraphTexts },
        }));

        setChapterEnhanceManifest((m) =>
          m && m.job_id === jobId
            ? {
                ...m,
                style_prompt,
                style_label,
                chunks_ready: ci + 1,
                enhanced_chunks: [...m.enhanced_chunks, { index: ci, text: parsed.text }],
              }
            : m,
        );
      }

      setChapterEnhanceManifest((m) =>
        m && m.job_id === jobId
          ? { ...m, status: "complete", chunks_ready: chunks.length, style_prompt, style_label }
          : m,
      );
      setEnhancementStatus("ready");
      setEnhancementScope("chapter");
      setReadingMode("enhanced");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Chapter enhancement failed.";
      setEnhancementStatus("error");
      setEnhancementError(msg);
      setChapterEnhanceManifest((m) => (m && m.job_id === jobId ? { ...m, status: "error", error: msg } : m));
    }
  }, [
    enhanceWebhookUrl,
    bookId,
    chapterSlug,
    enhancementPreset,
    customEnhancementPrompt,
    readerParagraphs,
  ]);

  const generateNarration = useCallback(
    async (scope: NarrationScope) => {
      if (!narrationWebhookUrl || narrationWebhookUrl.trim().length === 0) {
        setNarrationStatus("error");
        setNarrationAudioUrl(null);
        setNarrationWarning(null);
        setNarrationError("Narration webhook URL is missing. Set VITE_AISTORYCAST_NARRATION_WEBHOOK_URL.");
        return;
      }

      const spreadForNarration =
        readingMode === "enhanced" ? spreadPlainText.trim() || spreadPlainTextOriginal : spreadPlainTextOriginal;
      const storyText = scope === "section" ? spreadForNarration : chapterPlainTextForNarration;

      setNarrationScope(scope);
      setNarrationStatus("generating");
      setNarrationError(null);
      setNarrationWarning(null);

      try {
        const response = await fetch(narrationWebhookUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            book_id: bookId,
            chapter_id: chapterSlug,
            story_text: storyText,
            narration_scope: scope,
            readingMode,
          }),
        });

        const raw = await response.text();

        if (!raw.trim()) {
          throw new Error("Narration service returned an empty response.");
        }

        let payload: unknown;
        try {
          payload = JSON.parse(raw);
        } catch {
          throw new Error("Narration service returned invalid JSON.");
        }

        if (!response.ok) {
          const detail = formatNarrationWebhookErrorPayload(payload);
          if (detail) throw new Error(`Narration request failed (HTTP ${response.status}). ${detail}`);
          throw new Error(`Narration request failed (HTTP ${response.status}).`);
        }

        if (isObject(payload) && payload.success === false) {
          const detail = formatNarrationWebhookErrorPayload(payload) ?? "Narration failed.";
          throw new Error(detail);
        }

        if (isObject(payload) && typeof payload.warning === "string" && payload.warning.trim().length > 0) {
          setNarrationWarning(payload.warning.trim());
        } else {
          setNarrationWarning(null);
        }

        const audioUrl = parseNarrationAudioUrl(payload);
        if (!audioUrl) {
          const hint = narrationPayloadKeysHint(payload);
          throw new Error(
            `Narration response had no playable URL (expected audio_url or a non-empty audio_urls array). ` +
              `Received keys: ${hint}.`,
          );
        }

        if (isObject(payload) && payload.mode === "chunked") {
          const total =
            typeof payload.chunks_total === "number" && Number.isFinite(payload.chunks_total)
              ? Math.max(1, Math.floor(payload.chunks_total))
              : null;
          const ready =
            typeof payload.chunks_ready === "number" && Number.isFinite(payload.chunks_ready)
              ? Math.max(0, Math.floor(payload.chunks_ready))
              : null;
          const status = typeof payload.status === "string" ? payload.status.trim() : "";
          if (total !== null && total > 1) {
            const seg =
              ready !== null
                ? `This chapter was split into ${total} audio segments; the player below is the first segment (${ready} ready when the response returned).`
                : `This chapter was split into ${total} audio segments; the player below is the first segment.`;
            const tail =
              status === "processing"
                ? " Remaining segments may still be processing on the server."
                : "";
            setNarrationWarning((prev) => [prev, seg + tail].filter(Boolean).join(" "));
          }
        }

        setNarrationAudioUrl(audioUrl);
        setNarrationStatus("ready");
      } catch (error) {
        setNarrationStatus("error");
        setNarrationAudioUrl(null);
        setNarrationWarning(null);
        setNarrationError(error instanceof Error ? error.message : "Failed to generate narration.");
      }
    },
    [
      bookId,
      chapterSlug,
      chapterPlainTextForNarration,
      narrationWebhookUrl,
      readingMode,
      spreadPlainText,
      spreadPlainTextOriginal,
    ],
  );

  const spreadLabel =
    wide && rightPage && rightPage.length > 0
      ? `Spread ${Math.floor(clampedLeft / 2) + 1} · pages ${clampedLeft + 1}–${clampedLeft + 2}`
      : `Page ${clampedLeft + 1} of ${pages.length}`;

  const enhancedFootBadge =
    readingMode === "enhanced" &&
    ((enhancementScope === "chapter" && chapterEnhancement?.enhancedText?.trim()) ||
      (enhancementScope === "spread" && activeEnhancement?.enhancedText?.trim()))
      ? `Adapted · ${(enhancementScope === "chapter" ? chapterEnhancement?.styleLabel : activeEnhancement?.styleLabel) ?? "Enhanced"}`
      : null;

  const hideEnhancedParagraphLabels = Boolean(
    readingMode === "enhanced" &&
      ((enhancementScope === "spread" && activeEnhancement?.enhancedText?.trim()) ||
        (enhancementScope === "chapter" && chapterParagraphTexts)),
  );

  return (
    <div
      className="relative min-h-[100dvh] w-full overflow-x-hidden text-[#f0e6d4]"
      style={{
        background: [
          "radial-gradient(ellipse 72% 38% at 50% -2%, rgba(255,214,150,0.2) 0%, transparent 52%)",
          "radial-gradient(ellipse 80% 50% at 50% -8%, rgba(188,140,48,0.14) 0%, transparent 44%)",
          "radial-gradient(ellipse 95% 65% at 50% 108%, rgba(0,0,0,0.62) 0%, transparent 40%)",
          "linear-gradient(168deg, #0c0907 0%, #1e1610 34%, #14100c 72%, #080605 100%)",
        ].join(","),
      }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 62% 48% at 50% 38%, transparent 22%, rgba(0,0,0,0.58) 100%), repeating-linear-gradient(90deg, transparent, transparent 3px, rgba(0,0,0,0.024) 3px, rgba(0,0,0,0.024) 6px)",
          opacity: 0.55,
        }}
      />

      <div className="relative z-[1] mx-auto max-w-[min(100%,96rem)] px-3 pb-10 pt-5 sm:px-5 lg:px-8">
        <nav
          className="mb-5 flex flex-wrap items-center gap-2 text-xs text-[#b9a88a]"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          <Link to="/app" className="transition-colors hover:text-[#f0e6d4]">
            Home
          </Link>
          <span className="text-[#6b5c48]" aria-hidden>
            /
          </span>
          <Link to="/app/library" className="transition-colors hover:text-[#f0e6d4]">
            Library
          </Link>
          <span className="text-[#6b5c48]" aria-hidden>
            /
          </span>
          <span className="font-medium text-[#e8dcc8]">{bookTitle}</span>
          <span className="text-[#6b5c48]" aria-hidden>
            /
          </span>
          <span className="font-medium text-[#f5edd8]">{chapterHeading}</span>
        </nav>

        <div className="mx-auto grid w-full max-w-[min(100vw-0.5rem,96rem)] grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start lg:gap-8 xl:grid-cols-[minmax(0,1fr)_420px] xl:gap-10">
          {/* Main column: book + controls stay left; never overlap companion grid column */}
          <div className="book-hero-column relative z-0 flex min-w-0 flex-col items-stretch">
            <header className="mb-5 text-center lg:mb-6">
              <p
                className="text-[0.65rem] font-semibold uppercase tracking-[0.32em] text-[#9a8470]"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Now reading
              </p>
              <h1
                className="mx-auto mt-1.5 max-w-4xl text-2xl font-semibold leading-tight text-[#fdf6e9] drop-shadow sm:text-3xl"
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  textShadow: "0 2px 0 rgba(0,0,0,0.4), 0 12px 28px rgba(0,0,0,0.45)",
                }}
              >
                {chapterHeading}
              </h1>
              {author ? (
                <p className="mt-1.5 text-sm text-[#c4b49a]" style={{ fontFamily: "'Lora', serif" }}>
                  {author}
                </p>
              ) : null}
            </header>

            <section
              className="book-stage relative z-0 mx-auto flex w-full max-w-full min-w-0 flex-col items-center overflow-hidden px-0 sm:px-1"
              aria-label="Open book on desk"
            >
              <div
                className="desk-surface relative z-0 w-full max-w-full min-w-0 overflow-hidden rounded-[1.75rem] border border-black/60 px-3 pb-10 pt-7 sm:rounded-[2rem] sm:px-6 sm:pb-12 sm:pt-9"
                style={{
                  background: [
                    "linear-gradient(192deg, rgba(52,38,28,0.96) 0%, rgba(26,18,12,0.99) 40%, rgba(12,9,7,1) 100%)",
                    "repeating-linear-gradient(88deg, rgba(255,255,255,0.028) 0 1px, transparent 1px 12px)",
                    "radial-gradient(ellipse 130% 70% at 50% -5%, rgba(92,68,48,0.4) 0%, transparent 52%)",
                  ].join(","),
                  boxShadow:
                    "inset 0 2px 0 rgba(255,220,190,0.05), inset 0 -18px 32px rgba(0,0,0,0.48), 0 26px 52px rgba(0,0,0,0.48)",
                }}
              >
                <div
                  className="pedestal-shadow pointer-events-none absolute bottom-4 left-1/2 z-0 h-44 w-[min(100%,42rem)] max-w-full -translate-x-1/2 rounded-[50%] blur-2xl"
                  style={{
                    background: "radial-gradient(ellipse at center, rgba(205,160,48,0.26) 0%, rgba(95,65,32,0.1) 48%, transparent 74%)",
                  }}
                />
                <div
                  className="pedestal-shadow pointer-events-none absolute bottom-2 left-1/2 z-0 h-4 w-[min(100%,36rem)] max-w-[92%] -translate-x-1/2 rounded-sm"
                  style={{
                    background: "linear-gradient(to bottom, #5c4634, #241a12)",
                    boxShadow: "0 4px 0 rgba(0,0,0,0.45)",
                  }}
                />
                <div
                  className="pedestal-shadow pointer-events-none absolute bottom-0 left-1/2 z-0 h-[3.25rem] w-[min(100%,34rem)] max-w-[90%] -translate-x-1/2 rounded-b-lg"
                  style={{
                    background: "linear-gradient(to bottom, #3a2a1e 0%, #140e0b 58%, #060403 100%)",
                    boxShadow: "0 24px 48px rgba(0,0,0,0.78), inset 0 1px 0 rgba(255,255,255,0.05)",
                  }}
                />

                <div className="relative z-[1] w-full max-w-full min-w-0 pt-1" style={{ perspective: "1650px", perspectiveOrigin: "50% 12%" }}>
                  <div
                    className="relative mx-auto w-full max-w-full min-w-0"
                    style={{
                      transform: "rotateX(4.2deg)",
                      transformOrigin: "50% 94%",
                      transformStyle: "preserve-3d",
                    }}
                  >
                    <div
                      className="leather-cover pointer-events-none absolute inset-x-1 top-2 z-0 rounded-b-[40px] rounded-t-[24px] sm:inset-x-2 sm:top-3"
                      style={{
                        height: "calc(100% - 0.12rem)",
                        minHeight: "min(28vh, 270px)",
                        width: "auto",
                        background:
                          "linear-gradient(176deg, #4d3828 0%, #281810 20%, #0e0906 48%, #241610 74%, #52402c 100%)",
                        boxShadow:
                          "0 0 0 1px rgba(0,0,0,0.78), inset 0 3px 5px rgba(255,255,255,0.04), inset 0 -28px 42px rgba(0,0,0,0.7), 0 36px 72px rgba(0,0,0,0.7)",
                        transform: "translateZ(-30px) scale(1.02)",
                        transformStyle: "preserve-3d",
                      }}
                    />
                    <div
                      className="pointer-events-none absolute left-[1%] top-6 z-[1] hidden h-[82%] w-2.5 rounded-sm opacity-80 sm:block"
                      style={{
                        background: "linear-gradient(90deg, rgba(0,0,0,0.42) 0%, rgba(55,38,26,0.58) 100%)",
                        boxShadow: "inset -1px 0 3px rgba(0,0,0,0.55)",
                      }}
                    />
                    <div
                      className="pointer-events-none absolute right-[1%] top-6 z-[1] hidden h-[82%] w-2.5 rounded-sm opacity-80 sm:block"
                      style={{
                        background: "linear-gradient(270deg, rgba(0,0,0,0.42) 0%, rgba(55,38,26,0.58) 100%)",
                        boxShadow: "inset 1px 0 3px rgba(0,0,0,0.55)",
                      }}
                    />
                    <div
                      className="pointer-events-none absolute left-1/2 top-2 z-[2] h-3 w-[92%] max-w-full -translate-x-1/2 rounded-full opacity-50 blur-sm"
                      style={{ background: "linear-gradient(90deg, transparent, rgba(255,210,150,0.24), transparent)" }}
                    />

                    <div
                      className="relative z-[3] mx-auto w-full max-w-full min-w-0 overflow-visible rounded-t-[16px] rounded-b-[26px] p-[10px] sm:p-3"
                      style={{
                        background: "linear-gradient(165deg, rgba(22,15,10,0.99) 0%, rgba(8,6,5,1) 100%)",
                        boxShadow:
                          "0 0 0 1px rgba(185,145,48,0.14), 0 38px 76px rgba(0,0,0,0.58), inset 0 2px 0 rgba(255,255,255,0.03)",
                        transformStyle: "preserve-3d",
                      }}
                    >
                      <div
                        className="pointer-events-none absolute inset-[7px] rounded-t-[12px] rounded-b-[20px] opacity-[0.11]"
                        style={{ boxShadow: "inset 0 0 0 1px rgba(212,170,64,0.3)" }}
                      />

                      <div
                        className="relative max-w-full min-w-0 overflow-visible rounded-[12px] p-1 sm:p-1.5"
                        style={{
                          background:
                            "linear-gradient(168deg, #2e2016 0%, #16100c 46%, #20140e 100%), repeating-linear-gradient(-52deg, transparent, transparent 4px, rgba(0,0,0,0.06) 4px, rgba(0,0,0,0.06) 5px)",
                          boxShadow: "inset 0 3px 16px rgba(0,0,0,0.52), inset 0 -2px 0 rgba(255,255,255,0.02)",
                        }}
                      >
                        <div className="relative max-w-full min-w-0 overflow-visible" style={{ transformStyle: "preserve-3d" }}>
                          <BottomPageStackShadow />
                          <div className="relative max-w-full min-w-0 overflow-visible pb-2" style={{ transformStyle: "preserve-3d" }}>
                            <div className="relative flex max-w-full min-w-0 flex-row items-stretch gap-0 overflow-visible" style={{ transformStyle: "preserve-3d" }}>
                              <PageBlockColumn
                                side="left"
                                transform={leftBlockTransform}
                                transformOrigin="right center"
                                paragraphs={leftPage}
                                hideParagraphLabels={hideEnhancedParagraphLabels}
                                transformTransition={leftFlipListening ? flipTransition : undefined}
                                onTransformTransitionEnd={leftFlipListening ? onFlipTransitionEnd : undefined}
                                flipElevate={Boolean(leftFlipListening)}
                              />
                              {wide ? <CenterGutterValley /> : null}
                              {wide ? (
                                <PageBlockColumn
                                  side="right"
                                  transform={rightBlockTransform}
                                  transformOrigin="left center"
                                  paragraphs={rightPage ?? []}
                                  empty={!rightPage || rightPage.length === 0}
                                  hideParagraphLabels={hideEnhancedParagraphLabels}
                                  transformTransition={rightFlipListening ? flipTransition : undefined}
                                  onTransformTransitionEnd={rightFlipListening ? onFlipTransitionEnd : undefined}
                                  flipElevate={Boolean(rightFlipListening)}
                                />
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tactile page nav: subtle brass controls hugging the open spread */}
                <div className="pointer-events-none absolute inset-x-0 top-[18%] bottom-[30%] z-[24] flex items-center justify-between px-1 sm:px-3">
                  <button
                    type="button"
                    aria-label="Previous page"
                    title="Previous page"
                    onClick={() => beginPageFlip("prev")}
                    disabled={!canPrevPage || pageFlip !== null}
                    className="pointer-events-auto rounded-l-md rounded-r-sm border border-[#8a6a2a]/80 bg-gradient-to-b from-[#2e2418]/95 to-[#14100c]/98 px-2 py-6 text-lg font-semibold leading-none text-[#f0e6d4] shadow-[0_6px_16px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,214,160,0.12)] backdrop-blur-[2px] transition-[opacity,transform,border-color] hover:border-[#c9a227]/55 hover:brightness-110 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-35 sm:px-2.5 sm:py-8 sm:text-xl"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    aria-label="Next page"
                    title="Next page"
                    onClick={() => beginPageFlip("next")}
                    disabled={!canNextPage || pageFlip !== null}
                    className="pointer-events-auto rounded-l-sm rounded-r-md border border-[#8a6a2a]/80 bg-gradient-to-b from-[#2e2418]/95 to-[#14100c]/98 px-2 py-6 text-lg font-semibold leading-none text-[#f0e6d4] shadow-[0_6px_16px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,214,160,0.12)] backdrop-blur-[2px] transition-[opacity,transform,border-color] hover:border-[#c9a227]/55 hover:brightness-110 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-35 sm:px-2.5 sm:py-8 sm:text-xl"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    ›
                  </button>
                </div>
              </div>
            </section>

            <div className="relative z-[2] mx-auto mt-5 max-w-lg px-2 text-center">
              <p
                className="text-[0.68rem] tracking-wide text-[#7a6a58]"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {spreadLabel}
              </p>
              {enhancedFootBadge ? (
                <p
                  className="mt-1 text-[0.62rem] font-medium uppercase tracking-[0.22em] text-[#5c6b52]/95"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {enhancedFootBadge}
                </p>
              ) : null}
            </div>
          </div>

          <aside
            className="companion-panel relative z-20 mx-auto w-full min-w-0 max-w-full shrink-0 opacity-95 lg:mx-0 lg:mt-0 lg:w-full lg:max-w-none lg:pt-1 lg:opacity-100"
            aria-label="AIStoryCast companion"
          >
            <div
              className="relative z-10 flex min-h-0 w-full flex-col overflow-hidden rounded-xl border border-[#2a2218]/90 bg-gradient-to-b from-[#1a1510]/95 to-[#0d0a08]/98 p-3 shadow-md sm:rounded-2xl sm:p-4 lg:sticky lg:top-6 lg:max-h-[calc(100dvh-5rem)] lg:overflow-hidden lg:p-5"
              style={{
                boxShadow:
                  "inset 0 1px 0 rgba(255,214,160,0.07), 0 8px 24px rgba(0,0,0,0.38), 0 1px 3px rgba(0,0,0,0.42)",
              }}
            >
              <p
                className="text-[0.58rem] font-semibold uppercase tracking-[0.18em] text-[#8a7a68] sm:text-[0.62rem]"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Companion
              </p>
              <div
                role="tablist"
                aria-label="Companion sections"
                className="mt-3 flex flex-wrap gap-1 rounded-md border border-[#4a3820]/70 bg-[#0f0c09]/90 p-1 shadow-inner"
              >
                {(
                  [
                    ["chapters", "Chapters"],
                    ["characters", "Characters"],
                    ["storyGuide", "Story Guide"],
                    ["scene", "Scene"],
                    ["analysis", "Analysis"],
                    ["secrets", "Secrets"],
                  ] as const
                ).map(([id, label]) => {
                  const selected = companionTab === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      aria-controls={`companion-panel-${id}`}
                      id={`companion-tab-${id}`}
                      tabIndex={0}
                      onClick={() => setCompanionTab(id)}
                      className={[
                        "min-w-0 flex-1 rounded-md px-1.5 py-2 text-[0.62rem] font-semibold uppercase tracking-wide transition-colors sm:min-w-[4.5rem] sm:px-2 sm:text-[0.68rem]",
                        selected
                          ? "bg-[#b8921f] text-[#1a1308] shadow-sm"
                          : "bg-transparent text-[#9a8a78] hover:bg-[#1f1812]/90 hover:text-[#d4c4a8]",
                      ].join(" ")}
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>

              <div className="mt-4 flex min-h-0 flex-1 flex-col">
                {companionTab === "chapters" ? (
                  <div
                    role="tabpanel"
                    id="companion-panel-chapters"
                    aria-labelledby="companion-tab-chapters"
                    className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-0.5"
                  >
                    <section className="flex flex-col gap-2" aria-label="Bookmarks">
                      <h3
                        className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-[#bba98e]"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Your place
                      </h3>
                      <div className="flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={saveMyPlace}
                          className="w-full rounded-md border border-[#8a6a2a]/75 bg-gradient-to-b from-[#2e2418] to-[#1a140f] px-3 py-2.5 text-sm font-medium text-[#f0e6d4] shadow-sm transition-colors hover:border-[#c9a227]/55"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          Save my place
                        </button>
                        {savedBookmark ? (
                          <button
                            type="button"
                            onClick={resumeSavedPlace}
                            className="w-full rounded-md border border-[#9a7418]/80 bg-gradient-to-b from-[#5a4218] to-[#2a1a0c] px-3 py-2.5 text-sm font-semibold text-[#fdf6e9] shadow-sm transition-colors hover:from-[#6b4e1c] hover:to-[#342210]"
                            style={{ fontFamily: "'Inter', sans-serif" }}
                          >
                            Resume saved place
                          </button>
                        ) : null}
                        {savedBookmark ? (
                          <p className="text-[0.65rem] leading-snug text-[#7d6d58]" style={{ fontFamily: "'Inter', sans-serif" }}>
                            Saved{" "}
                            {(() => {
                              try {
                                return new Date(savedBookmark.savedAt).toLocaleString(undefined, {
                                  dateStyle: "short",
                                  timeStyle: "short",
                                });
                              } catch {
                                return savedBookmark.savedAt;
                              }
                            })()}
                            {" · "}
                            {chapterTitleBySlug.get(savedBookmark.chapterSlug) ?? savedBookmark.chapterSlug}
                          </p>
                        ) : (
                          <p className="text-[0.65rem] text-[#6a5c4e]" style={{ fontFamily: "'Inter', sans-serif" }}>
                            No saved place for this book yet.
                          </p>
                        )}
                      </div>
                    </section>

                    <section className="flex flex-col gap-2" aria-label="Chapters">
                      <h3
                        className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-[#bba98e]"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Chapters
                      </h3>
                      {chapterList.length === 0 ? (
                        <p className="text-xs text-[#7d6d58]" style={{ fontFamily: "'Inter', sans-serif" }}>
                          Chapter list unavailable for this title.
                        </p>
                      ) : (
                        <ul className="flex flex-col gap-1 rounded-lg border border-[#4a3820]/55 bg-[#110d0a]/88 p-1.5">
                          {chapterList.map((ch) => {
                            const current = ch.slug === chapterSlug;
                            return (
                              <li key={ch.slug}>
                                <Link
                                  to={readerChapterHref(bookId, ch.slug)}
                                  className={[
                                    "block rounded-md px-2.5 py-2 text-left text-xs transition-colors sm:text-[0.8rem]",
                                    current
                                      ? "border border-[#c9a227]/50 bg-[#2a2214] font-semibold text-[#f5edd8]"
                                      : "border border-transparent text-[#c9b8a0] hover:border-[#6b5428]/50 hover:bg-[#1a1510]",
                                  ].join(" ")}
                                  style={{ fontFamily: "'Inter', sans-serif" }}
                                >
                                  {ch.title}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      <Link
                        to="/app/library"
                        className="text-center text-[0.7rem] font-medium text-[#b8921f] underline-offset-2 hover:text-[#d4c48a] hover:underline"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Back to Library
                      </Link>
                    </section>

                    <section className="flex min-h-0 flex-1 flex-col gap-2" aria-label="Search in book">
                      <h3
                        className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-[#bba98e]"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Search
                      </h3>
                      <input
                        type="search"
                        value={sidebarSearchQuery}
                        onChange={(e) => setSidebarSearchQuery(e.target.value)}
                        placeholder="Search this book…"
                        autoComplete="off"
                        className="w-full rounded-md border border-[#5c4a2a]/70 bg-[#0c0a08] px-3 py-2 text-sm text-[#f0e6d4] placeholder:text-[#6a5c4e] focus:border-[#c9a227]/55 focus:outline-none focus:ring-1 focus:ring-[#c9a227]/35"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      />
                      {bookSearchLoading ? (
                        <p className="text-xs text-[#8a7a66]" style={{ fontFamily: "'Inter', sans-serif" }}>
                          Indexing chapters for search…
                        </p>
                      ) : null}
                      {!bookSearchLoading && sidebarSearchQuery.trim().length >= 2 && searchMatches.length === 0 ? (
                        <p className="text-xs text-[#7d6d58]" style={{ fontFamily: "'Inter', sans-serif" }}>
                          No matches in indexed chapters.
                        </p>
                      ) : null}
                      {searchMatches.length > 0 ? (
                        <ul className="flex max-h-48 min-h-0 flex-col gap-1 overflow-y-auto rounded-lg border border-[#4a3820]/55 bg-[#110d0a]/88 p-1.5 sm:max-h-56">
                          {searchMatches.map(({ block, snippet }, i) => (
                            <li key={`${block.chapterSlug}-${block.paragraphIndex}-${i}`}>
                              <button
                                type="button"
                                onClick={() => jumpToSearchBlock(block)}
                                className="w-full rounded-md border border-transparent px-2 py-2 text-left transition-colors hover:border-[#6b5428]/55 hover:bg-[#1a1510]"
                              >
                                <span
                                  className="block text-[0.62rem] font-semibold uppercase tracking-wide text-[#a89478]"
                                  style={{ fontFamily: "'Inter', sans-serif" }}
                                >
                                  {chapterTitleBySlug.get(block.chapterSlug) ?? block.chapterSlug}
                                  {block.chapterSlug === chapterSlug ? "" : " · other chapter"}
                                </span>
                                <span
                                  className="mt-0.5 line-clamp-3 text-[0.75rem] leading-snug text-[#d4c4a8]"
                                  style={{ fontFamily: "'Lora', serif" }}
                                >
                                  {snippet}
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      {sidebarSearchQuery.trim().length > 0 && sidebarSearchQuery.trim().length < 2 ? (
                        <p className="text-[0.65rem] text-[#6a5c4e]" style={{ fontFamily: "'Inter', sans-serif" }}>
                          Type at least 2 characters to search.
                        </p>
                      ) : null}
                    </section>
                  </div>
                ) : null}

                {companionTab === "characters" ? (
                  <CastCharactersPanel
                    bookId={bookId}
                    bookTitle={bookTitle}
                    author={author}
                    castAnalysisEligible={castAnalysisEligible}
                  />
                ) : null}

                {companionTab === "scene" ? (
                  <div
                    role="tabpanel"
                    id="companion-panel-scene"
                    aria-labelledby="companion-tab-scene"
                    className="flex min-h-0 flex-1 flex-col gap-4"
                  >
                    {sceneLoading ? (
                      <p className="text-sm leading-relaxed text-[#c4b4a0]" style={{ fontFamily: "'Inter', sans-serif" }}>
                        Generating illustration…
                      </p>
                    ) : null}
                    {sceneError ? (
                      <p className="text-sm leading-relaxed text-amber-200/95" style={{ fontFamily: "'Inter', sans-serif" }}>
                        {sceneError}
                      </p>
                    ) : null}
                    {sceneUrl ? (
                      <>
                        <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-lg border-2 border-[#6b5428]/70 bg-[#070504] p-1.5 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.6),0_4px_14px_rgba(0,0,0,0.45)] sm:p-2">
                          <img
                            src={sceneUrl}
                            alt="Illustration for this spread"
                            className="mx-auto h-auto w-full max-h-[min(42vh,360px)] rounded-sm object-contain shadow-lg sm:max-h-[min(48vh,400px)] lg:max-h-[min(52vh,440px)]"
                          />
                        </div>
                        {sceneFilename ? (
                          <p
                            className="truncate text-xs text-[#8a7a66]"
                            style={{ fontFamily: "'Inter', sans-serif" }}
                            title={sceneFilename}
                          >
                            {sceneFilename}
                          </p>
                        ) : null}
                      </>
                    ) : !sceneLoading && !sceneError ? (
                      <p className="text-sm leading-relaxed text-[#b0a090]" style={{ fontFamily: "'Lora', serif" }}>
                        Generate an illustration from the current open pages.
                      </p>
                    ) : null}
                    <button
                      type="button"
                      onClick={onShowScene}
                      disabled={sceneLoading || spreadPlainText.trim().length === 0}
                      className="mt-auto w-full rounded-md border border-[#9a7418]/85 bg-gradient-to-b from-[#6b4e1c] to-[#342210] px-4 py-3 text-sm font-semibold text-[#fdf6e9] shadow-md transition-colors hover:from-[#7a5a22] hover:to-[#403018] disabled:cursor-not-allowed disabled:opacity-40"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {sceneLoading ? "Generating…" : "Show this scene"}
                    </button>
                  </div>
                ) : null}

                {companionTab === "storyGuide" ? (
                  <div
                    role="tabpanel"
                    id="companion-panel-storyGuide"
                    aria-labelledby="companion-tab-storyGuide"
                    className="flex min-h-0 flex-1 flex-col gap-5"
                  >
                    <section className="flex flex-col gap-2.5" aria-label="Story Guide controls">
                      <h3
                        className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-[#bba98e]"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Story Guide controls
                      </h3>
                      <div className="rounded-lg border border-[#4a3820]/65 bg-[#110d0a]/85 p-3">
                        <p className="text-[0.62rem] font-semibold uppercase tracking-[0.12em] text-[#9f917b]" style={{ fontFamily: "'Inter', sans-serif" }}>
                          Status
                        </p>
                        <div className="mt-2 flex items-center justify-between gap-2 text-sm text-[#d9cab2]" style={{ fontFamily: "'Inter', sans-serif" }}>
                          <span className="capitalize">{storyGuideStatus}</span>
                          <span className="rounded border border-[#6b5428]/80 bg-[#1b140f] px-2 py-0.5 text-[0.68rem] uppercase tracking-wide text-[#d8bf8a]">
                            {storyGuideMode}
                          </span>
                        </div>
                        <p className="mt-2 text-xs text-[#9f917b]" style={{ fontFamily: "'Inter', sans-serif" }}>
                          Current reading paragraph: #{storyGuideParagraphIndex + 1}
                        </p>
                      </div>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <button
                          type="button"
                          onClick={() => setStoryGuideStatus("reading")}
                          className="rounded-md border border-[#8a6a2a]/75 bg-gradient-to-b from-[#2e2418] to-[#1a140f] px-3 py-2 text-sm font-medium text-[#f0e6d4] transition-colors hover:border-[#c9a227]/55"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          Read with Story Guide
                        </button>
                        <button
                          type="button"
                          onClick={() => setStoryGuideStatus("paused")}
                          className="rounded-md border border-[#8a6a2a]/75 bg-gradient-to-b from-[#2e2418] to-[#1a140f] px-3 py-2 text-sm font-medium text-[#f0e6d4] transition-colors hover:border-[#c9a227]/55"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          Pause
                        </button>
                        <button
                          type="button"
                          onClick={() => setStoryGuideStatus("reading")}
                          className="rounded-md border border-[#8a6a2a]/75 bg-gradient-to-b from-[#2e2418] to-[#1a140f] px-3 py-2 text-sm font-medium text-[#f0e6d4] transition-colors hover:border-[#c9a227]/55 sm:col-span-2"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          Continue
                        </button>
                      </div>
                    </section>

                    <section className="flex flex-col gap-2.5" aria-label="Ask and quick actions">
                      <h3
                        className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-[#bba98e]"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Ask & quick actions
                      </h3>
                      <div className="rounded-lg border border-[#4a3820]/65 bg-[#0f0c09]/85 p-3">
                        <label htmlFor="story-guide-question" className="mb-2 block text-[0.62rem] uppercase tracking-[0.12em] text-[#b8a68a]" style={{ fontFamily: "'Inter', sans-serif" }}>
                          Your question
                        </label>
                        <textarea
                          id="story-guide-question"
                          value={storyGuideQuestion}
                          onChange={(event) => setStoryGuideQuestion(event.target.value)}
                          rows={3}
                          placeholder="Ask about this page..."
                          className="w-full rounded-md border border-[#5b4830] bg-[#1a130d] px-2.5 py-2 text-sm text-[#f0e6d4] outline-none placeholder:text-[#7d6d58] focus:border-[#b8921f]"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        />
                        <button
                          type="button"
                          onClick={askStoryGuide}
                          className="mt-2 w-full rounded-md border border-[#8a6a2a]/80 bg-gradient-to-b from-[#2f2418] to-[#1a140f] px-3 py-2 text-sm font-semibold text-[#f0e6d4] transition-colors hover:border-[#c9a227]/55"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          Ask
                        </button>
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                        <button
                          type="button"
                          onClick={() => quickStoryGuideAnswer("explain")}
                          className="rounded-md border border-[#6f5626]/70 bg-[#17120e] px-3 py-2 text-sm text-[#d8cab2] transition-colors hover:bg-[#201811]"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          Explain this
                        </button>
                        <button
                          type="button"
                          onClick={() => quickStoryGuideAnswer("simple")}
                          className="rounded-md border border-[#6f5626]/70 bg-[#17120e] px-3 py-2 text-sm text-[#d8cab2] transition-colors hover:bg-[#201811]"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          Read it like I'm 5
                        </button>
                        <button
                          type="button"
                          onClick={() => quickStoryGuideAnswer("summary")}
                          className="rounded-md border border-[#6f5626]/70 bg-[#17120e] px-3 py-2 text-sm text-[#d8cab2] transition-colors hover:bg-[#201811]"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          Summarize this page
                        </button>
                      </div>
                      {storyGuideAnswer ? (
                        <div className="rounded-lg border border-[#4a3820]/65 bg-[#110d0a]/85 p-3">
                          <p className="text-[0.62rem] font-semibold uppercase tracking-[0.12em] text-[#b8a68a]" style={{ fontFamily: "'Inter', sans-serif" }}>
                            Answer
                          </p>
                          <p className="mt-1 text-sm leading-relaxed text-[#d7c7af]" style={{ fontFamily: "'Lora', serif" }}>
                            {storyGuideAnswer}
                          </p>
                        </div>
                      ) : null}
                    </section>

                    <section className="flex flex-col gap-2.5" aria-label="Narration">
                      <h3
                        className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-[#bba98e]"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Narration
                      </h3>
                      <div className="flex flex-wrap gap-1.5 rounded-md border border-[#5c4a2a]/55 bg-[#140f0c]/90 p-1 shadow-inner">
                        <button
                          type="button"
                          aria-pressed={readingMode === "exact"}
                          onClick={() => setReadingMode("exact")}
                          className={[
                            "rounded-md px-2.5 py-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.1em] transition-colors",
                            readingMode === "exact"
                              ? "bg-[#b8921f] text-[#1a1308] shadow-sm"
                              : "bg-transparent text-[#9a8a78] hover:bg-[#1f1812]/90 hover:text-[#d4c4a8]",
                          ].join(" ")}
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          Exact
                        </button>
                        <button
                          type="button"
                          aria-pressed={readingMode === "enhanced"}
                          onClick={() => setReadingMode("enhanced")}
                          className={[
                            "rounded-md px-2.5 py-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.1em] transition-colors",
                            readingMode === "enhanced"
                              ? "bg-[#b8921f] text-[#1a1308] shadow-sm"
                              : "bg-transparent text-[#9a8a78] hover:bg-[#1f1812]/90 hover:text-[#d4c4a8]",
                          ].join(" ")}
                          style={{ fontFamily: "'Inter', sans-serif" }}
                          title="Show adapted text after you generate for this spread"
                        >
                          Enhanced
                        </button>
                      </div>
                      <div className="flex flex-col gap-2 rounded-md border border-[#5c4a2a]/50 bg-[#110d0a]/85 p-2.5">
                        <p
                          className="text-[0.62rem] font-semibold uppercase tracking-[0.12em] text-[#9f917b]"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                          id="enhancement-scope-label"
                        >
                          Enhance scope
                        </p>
                        <div
                          className="flex flex-col gap-1.5"
                          role="radiogroup"
                          aria-labelledby="enhancement-scope-label"
                        >
                          <label className="flex cursor-pointer items-center gap-2 text-xs text-[#d8cab2]" style={{ fontFamily: "'Inter', sans-serif" }}>
                            <input
                              type="radio"
                              name="enhancement-scope"
                              className="accent-[#b8921f]"
                              checked={enhancementScope === "spread"}
                              onChange={() => {
                                setEnhancementScope("spread");
                                setEnhancementError(null);
                              }}
                            />
                            This spread
                          </label>
                          <label className="flex cursor-pointer items-center gap-2 text-xs text-[#d8cab2]" style={{ fontFamily: "'Inter', sans-serif" }}>
                            <input
                              type="radio"
                              name="enhancement-scope"
                              className="accent-[#b8921f]"
                              checked={enhancementScope === "chapter"}
                              onChange={() => {
                                setEnhancementScope("chapter");
                                setEnhancementError(null);
                              }}
                            />
                            Full chapter
                          </label>
                          <p className="pl-5 text-[0.65rem] text-[#6a5c4e]" style={{ fontFamily: "'Inter', sans-serif" }} title="Coming soon">
                            Full book — coming soon
                          </p>
                        </div>
                        <label className="text-[0.62rem] font-semibold uppercase tracking-[0.12em] text-[#9f917b]" style={{ fontFamily: "'Inter', sans-serif" }} htmlFor="enhancement-preset">
                          Style preset
                        </label>
                        <select
                          id="enhancement-preset"
                          value={enhancementPreset}
                          onChange={(e) => {
                            setEnhancementPreset(e.target.value as EnhancementPreset);
                            setEnhancementError(null);
                          }}
                          className="w-full rounded-md border border-[#5c4a2a]/70 bg-[#0c0a08] px-2 py-2 text-xs text-[#f0e6d4] focus:border-[#c9a227]/55 focus:outline-none"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          {ENHANCEMENT_PRESET_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        {enhancementPreset === "custom" ? (
                          <textarea
                            id="enhancement-custom"
                            value={customEnhancementPrompt}
                            onChange={(e) => setCustomEnhancementPrompt(e.target.value)}
                            rows={2}
                            placeholder="Describe the style (e.g. noir detective, Victorian formal)…"
                            className="w-full rounded-md border border-[#5b4830] bg-[#1a130d] px-2 py-2 text-xs text-[#f0e6d4] placeholder:text-[#6a5c4e] focus:border-[#b8921f] focus:outline-none"
                            style={{ fontFamily: "'Inter', sans-serif" }}
                          />
                        ) : null}
                        <button
                          type="button"
                          onClick={() => {
                            if (enhancementScope === "chapter") void generateChapterEnhancement();
                            else void generateEnhancedSpread();
                          }}
                          disabled={
                            enhancementStatus === "generating" ||
                            enhancementScope === "book" ||
                            (enhancementScope === "spread" && spreadPlainTextOriginal.trim().length === 0) ||
                            (enhancementScope === "chapter" && chapterPlainText.trim().length === 0) ||
                            (enhancementPreset === "custom" && customEnhancementPrompt.trim().length === 0)
                          }
                          className="w-full rounded-md border border-[#8a6a2a]/80 bg-gradient-to-b from-[#4a3818] to-[#22180c] px-3 py-2.5 text-sm font-semibold text-[#fdf6e9] shadow-sm transition-colors hover:from-[#5c461c] hover:to-[#2a1e10] disabled:cursor-not-allowed disabled:opacity-40"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          {enhancementStatus === "generating" ? "Generating…" : "Generate Enhanced"}
                        </button>
                        {enhancementStatus === "generating" ? (
                          <p className="text-xs text-[#bcae98]" style={{ fontFamily: "'Inter', sans-serif" }}>
                            {enhancementScope === "chapter" && chapterEnhanceManifest
                              ? `Enhancing chapter… ${chapterEnhanceManifest.chunks_ready}/${chapterEnhanceManifest.chunks_total}`
                              : "Adapting this spread…"}
                          </p>
                        ) : null}
                        {enhancementStatus === "error" && enhancementError ? (
                          <p className="text-xs text-amber-200/95" style={{ fontFamily: "'Inter', sans-serif" }} role="alert">
                            {enhancementError}
                          </p>
                        ) : null}
                        {enhancementScope === "spread" &&
                        activeEnhancement?.enhancedText &&
                        enhancementStatus !== "generating" ? (
                          <p className="text-[0.65rem] text-[#8a9f78]" style={{ fontFamily: "'Inter', sans-serif" }}>
                            {readingMode === "enhanced"
                              ? "Viewing adapted spread on the page."
                              : `Ready · ${activeEnhancement.styleLabel} — switch to Enhanced to view`}
                          </p>
                        ) : enhancementScope === "chapter" &&
                          chapterEnhancement?.enhancedText &&
                          enhancementStatus !== "generating" ? (
                          <p className="text-[0.65rem] text-[#8a9f78]" style={{ fontFamily: "'Inter', sans-serif" }}>
                            {readingMode === "enhanced"
                              ? `Viewing adapted chapter (${chapterEnhanceManifest?.chunks_total ?? "…"} chunks).`
                              : `Ready · ${chapterEnhancement.styleLabel} — switch to Enhanced to view`}
                          </p>
                        ) : readingMode === "enhanced" && enhancementScope === "spread" && !activeEnhancement?.enhancedText ? (
                          <p className="text-[0.65rem] text-[#7d6d58]" style={{ fontFamily: "'Inter', sans-serif" }}>
                            Generate for this spread to see adapted text on the page.
                          </p>
                        ) : readingMode === "enhanced" && enhancementScope === "chapter" && !chapterEnhancement?.enhancedText ? (
                          <p className="text-[0.65rem] text-[#7d6d58]" style={{ fontFamily: "'Inter', sans-serif" }}>
                            Generate full chapter to see adapted text while you read.
                          </p>
                        ) : null}
                      </div>
                      <div className="flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => generateNarration("section")}
                          disabled={narrationStatus === "generating" || spreadPlainText.trim().length === 0}
                          className="w-full rounded-md border border-[#9a7418]/80 bg-gradient-to-b from-[#6b4e1c] to-[#342210] px-3 py-2.5 text-sm font-semibold text-[#fdf6e9] shadow-sm transition-colors hover:from-[#7a5a22] hover:to-[#403018] disabled:cursor-not-allowed disabled:opacity-40"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                          title="Generate narration audio for current open pages"
                        >
                          {narrationStatus === "generating" && narrationScope === "section"
                            ? "Generating section…"
                            : "Narrate this section"}
                        </button>
                        <button
                          type="button"
                          onClick={() => generateNarration("chapter")}
                          disabled={narrationStatus === "generating" || chapterPlainText.trim().length === 0}
                          className="w-full rounded-md border border-[#9a7418]/80 bg-gradient-to-b from-[#5a4218] to-[#2a1a0c] px-3 py-2.5 text-sm font-semibold text-[#fdf6e9] shadow-sm transition-colors hover:from-[#6b4e1c] hover:to-[#342210] disabled:cursor-not-allowed disabled:opacity-40"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                          title="Generate narration audio for the entire chapter"
                        >
                          {narrationStatus === "generating" && narrationScope === "chapter"
                            ? "Generating chapter…"
                            : "Narrate full chapter"}
                        </button>
                      </div>
                      {narrationStatus === "generating" ? (
                        <p className="text-xs text-[#bcae98]" style={{ fontFamily: "'Inter', sans-serif" }}>
                          {narrationScope === "chapter" ? "Generating chapter narration…" : "Generating section narration…"}
                        </p>
                      ) : null}
                      {narrationWarning ? (
                        <p className="text-xs text-[#9a9078]" style={{ fontFamily: "'Inter', sans-serif" }} role="status">
                          {narrationWarning}
                        </p>
                      ) : null}
                      {narrationStatus === "error" && narrationError ? (
                        <p className="text-xs text-amber-200/95" style={{ fontFamily: "'Inter', sans-serif" }}>
                          {narrationError}
                        </p>
                      ) : null}
                    </section>

                    <section className="flex flex-col gap-2" aria-label="Audio playback">
                      <h3
                        className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-[#bba98e]"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Audio
                      </h3>
                      {narrationStatus === "ready" && narrationAudioUrl ? (
                        <audio controls src={narrationAudioUrl} className="w-full rounded-md" />
                      ) : (
                        <p className="text-xs text-[#7d6d58]" style={{ fontFamily: "'Inter', sans-serif" }}>
                          Generate narration above to play audio here.
                        </p>
                      )}
                    </section>
                  </div>
                ) : null}

                {companionTab === "analysis" ? (
                  <div
                    role="tabpanel"
                    id="companion-panel-analysis"
                    aria-labelledby="companion-tab-analysis"
                    className="flex min-h-0 flex-1 flex-col gap-4"
                  >
                    <p className="text-sm leading-relaxed text-[#b0a090]" style={{ fontFamily: "'Lora', serif" }}>
                      Analysis coming next. AIStoryCast will explain this page, summarize the action, and highlight important context.
                    </p>
                    <button
                      type="button"
                      disabled
                      className="w-full cursor-not-allowed rounded-md border border-[#5c4a2a]/50 bg-[#120e0c]/85 px-4 py-3 text-sm font-medium text-[#6a5c4e]"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                      title="Coming next"
                    >
                      Analyze this page
                    </button>
                  </div>
                ) : null}

                {companionTab === "secrets" ? (
                  <div
                    role="tabpanel"
                    id="companion-panel-secrets"
                    aria-labelledby="companion-tab-secrets"
                    className="flex min-h-0 flex-1 flex-col gap-4"
                  >
                    <p className="text-sm leading-relaxed text-[#b0a090]" style={{ fontFamily: "'Lora', serif" }}>
                      Secrets coming next. This will reveal symbolism, foreshadowing, literary references, and hidden details in the current passage.
                    </p>
                    <button
                      type="button"
                      disabled
                      className="w-full cursor-not-allowed rounded-md border border-[#5c4a2a]/50 bg-[#120e0c]/85 px-4 py-3 text-sm font-medium text-[#6a5c4e]"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                      title="Coming next"
                    >
                      Reveal secrets
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
